import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import InputBase from '@mui/material/InputBase'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormControl, Select, MenuItem, FormHelperText } from '@mui/material'

export const CustomDropdownSelector = React.memo(
  function CustomDropdownSelector({
    options,
    value,
    onSelect,
    label = 'Seleccione una opción*',
    getOptionLabel,
    getOptionValue,
    error,
    name = 'select',
    unselectableOption,
  }) {
    const selectedOptionIds = useMemo(() => {
      return value?.map(getOptionValue) || []
    }, [value, getOptionValue])

    const handleOptionClick = item => {
      const isUnselectable =
        unselectableOption &&
        getOptionValue(item) === getOptionValue(unselectableOption)

      if (!isUnselectable) {
        onSelect(item)
      }
    }

    return (
      <FormControl fullWidth error={Boolean(error)} variant="standard">
        <Select
          labelId={`${name}-label`}
          id={name}
          multiple
          value={selectedOptionIds}
          input={<StyledInputBase />}
          IconComponent={props => (
            <FontAwesomeIcon
              {...props}
              icon={faChevronDown}
              style={{ fontSize: '1.5rem', marginRight: '16px' }}
            />
          )}
          MenuProps={{
            MenuListProps: {
              sx: {
                padding: 0,
                backgroundColor: '#fff',
                width: '100%',
              },
            },
          }}
          renderValue={selectedIds => {
            const selectedOptions = options.filter(option =>
              selectedIds.includes(getOptionValue(option))
            )
            return (
              <p style={{ fontSize: '14px' }}>
                {selectedOptions.map(getOptionLabel).join(', ')}
              </p>
            )
          }}
          sx={{
            // Aquí haces que la flecha gire al abrir el menú (igual que MUI)
            '& .MuiSelect-icon': {
              transition: 'transform 250ms ease',
            },
            '&.Mui-focused .MuiSelect-icon, &.MuiSelect-open .MuiSelect-icon': {
              transform: 'rotate(180deg)',
            },
          }}
          data-testid={name ? `input-select-${name}` : 'input-select'}>
          {options.map(option => {
            const optionId = getOptionValue(option)
            const isSelected = selectedOptionIds.includes(optionId)
            const isUnselectable =
              unselectableOption &&
              optionId === getOptionValue(unselectableOption)

            return (
              <MenuItem
                key={optionId}
                value={optionId}
                style={{ padding: 0 }}
                onClick={() => handleOptionClick(option)}
                data-testid={
                  optionId
                    ? `btn-select-service-${optionId}`
                    : 'btn-select-service'
                }>
                <div
                  className={`service-card-option d-flex flex-row
                  ${isSelected ? 'selected-option' : ''}
                  ${isUnselectable ? 'unselectable-option' : ''}`}>
                  <p>{option.name}</p>
                  <p
                    className="opacity-50"
                    style={{ marginLeft: 8, marginRight: 8, flex: 1 }}>
                    {option.systemCode}
                  </p>
                  <p className="me-4 fw-semibold">
                    {option.FinalPrice ? `$${option.FinalPrice}` : ''}
                  </p>
                </div>
              </MenuItem>
            )
          })}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    )
  }
)

CustomDropdownSelector.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  error: PropTypes.string,
  name: PropTypes.string,
  unselectableOption: PropTypes.object,
}

const StyledInputBase = props => (
  <InputBase
    {...props}
    sx={{
      'label + &': {
        mt: 1.5,
      },
      position: 'relative',
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'grey.400',
      borderRadius: 1,
      padding: '10.5px 14px',
      fontSize: '1rem',
      transition: theme =>
        theme.transitions.create(['border-color', 'box-shadow']),
      '& .MuiSelect-select.MuiInputBase-input:focus': {
        backgroundColor: 'transparent',
        borderRadius: '4px',
      },
    }}
  />
)
